import { ReactNode } from 'react';

import { TFunction } from '@mint-lib/i18n';
import { typographyVariants } from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';
import {
  addMinutes,
  differenceInDays,
  differenceInSeconds,
  format,
} from 'date-fns';

import type {
  DataFileClientStepNames,
  DataFilePreview,
  DataFilePreviewV1,
  DataFilePreviewV2,
  DataFileStatus,
  TableDataFileItem,
} from '../api/types.js';
import { DateTimeSpan } from '../components/index.js';

export const getDataFileClientStep = (
  dataFilePreview: DataFilePreview,
): DataFileClientStepNames => {
  const {
    currentStep,
    status,
    isFileUploaded,
    templateSheetsMismatched,
    templateHeadersMismatched,
  } = dataFilePreview;

  if (currentStep === 'upload') {
    const isUploading = status === 'uploading';
    const isUploadStep =
      (!isFileUploaded && !isUploading) ||
      templateSheetsMismatched ||
      templateHeadersMismatched;

    return isUploadStep ? 'upload' : 'header_selection';
  }

  return currentStep;
};

export const checkIsNewItem = (importDate: string): boolean => {
  const diffInDays = differenceInDays(new Date(), new Date(importDate));
  return diffInDays < 1;
};

export const getRowClassName = ({
  disabled,
  createdAt,
}: {
  disabled: boolean;
  createdAt: string;
}) => {
  const diffInSeconds = differenceInSeconds(
    addMinutes(new Date(), new Date().getTimezoneOffset()),
    new Date(createdAt),
  );

  return cn({
    'text-text-disabled!': disabled,
    'bg-background-success!': diffInSeconds <= 60,
  });
};

interface StatusContent {
  label: string;
  color: 'secondary' | 'silver' | 'warning' | 'info' | 'success' | 'error';
  description?: string | ReactNode;
  toBeArchived?: boolean;
  disabled?: boolean;
  editable?: boolean;
}

const getStatusesData = (t: TFunction, dataFile: TableDataFileItem) => {
  const { scheduledArchiving, scheduledDeleting, previousStatus } = dataFile;
  const toBeArchivedStatus = scheduledArchiving ? (
    <span className={typographyVariants({ variant: 'bodyShort01' })}>
      {t('This file will be automatically archived on')}&nbsp;
      <DateTimeSpan date={scheduledArchiving} />
    </span>
  ) : null;

  const statusesData = {
    draft: {
      label: t('Draft'),
      color: 'silver',
      description: toBeArchivedStatus,
      toBeArchived: true,
      editable: true,
    },
    uncompleted: {
      label: t('Uncompleted'),
      color: 'warning',
      description: toBeArchivedStatus,
      toBeArchived: true,
      editable: true,
    },
    get uploading() {
      return this.uncompleted;
    },
    get validating() {
      return this.uncompleted;
    },
    loading: {
      label: t('Loading'),
      color: 'info',
      description: t(
        'We are currently processing data to display it in Reporting',
      ),
    },
    uploaded: {
      label: t('Uploaded'),
      color: 'success',
      description: t('Data has been successfully imported to Reporting'),
    },
    overwritten: {
      label: t('Overwritten'),
      color: 'secondary',
      description: t(
        'This file was overwritten by a newer upload related to the same data. It will be archived automatically on {{scheduledArchiving}} at {{scheduledArchivingTime}}.',
        {
          scheduledArchiving: format(
            scheduledArchiving as string,
            'dd/MM/yyyy',
          ),
          scheduledArchivingTime: format(scheduledArchiving as string, 'HH:mm'),
        },
      ),
      toBeArchived: true,
    },
    failed: {
      label: t('Failed'),
      color: 'error',
      description: toBeArchivedStatus,
      toBeArchived: true,
    },
    deleting: {
      label: t('Deleting'),
      color: 'warning',
      description: t(
        'File deletion is in progress. It may take a couple of hours',
      ),
      disabled: true,
    },
    archived: {
      label: t('Archived'),
      color: 'silver',
      description: scheduledDeleting ? (
        <span className={typographyVariants({ variant: 'bodyShort01' })}>
          {t(
            'This file was in {{previousStatus}} status; it will be permanently deleted on',
            {
              previousStatus,
            },
          )}
          &nbsp;
          <DateTimeSpan date={scheduledDeleting} />
        </span>
      ) : null,
      disabled: true,
    },
  } as const satisfies Record<DataFileStatus, StatusContent>;

  return statusesData;
};

export const getDataFileStatusContent = (
  t: TFunction,
  dataFile: TableDataFileItem,
): StatusContent => {
  const statusesData = getStatusesData(t, dataFile);

  return statusesData[dataFile.status];
};

export function getIsDataFileContextFilled({
  context,
  isImportHubV2,
  isPricingItemLevel,
}: {
  context: DataFilePreview['context'];
  isImportHubV2: boolean;
  isPricingItemLevel?: boolean;
}) {
  const requiredFields = isImportHubV2
    ? ([
        'mediaPlanId',
        'mediaPlanName',
        'activityId',
        'activityName',
      ] as (keyof DataFilePreviewV2['context'])[])
    : (['journeyId', 'journeyName'] as (keyof DataFilePreviewV1['context'])[]);

  if (isPricingItemLevel) {
    requiredFields.push('platformName');
  }

  return requiredFields.every((field) =>
    Boolean(context[field as keyof typeof context]),
  );
}

export function getPreviewAfterStepChange({
  preview,
  nextStep,
  isStepBackward,
}: {
  preview: DataFilePreview;
  nextStep: DataFilePreview['currentStep'];
  isStepBackward: boolean;
}): DataFilePreview {
  return {
    ...preview,
    currentStep: nextStep,
    ...(isStepBackward && { isEdited: false }),
  };
}

export function isDataFileRemovable(status: DataFileStatus): boolean {
  return status !== 'loading' && status !== 'deleting';
}
